import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  useMediaQuery,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';

function formatDateTR(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleString('tr-TR', {
    timeZone: 'UTC',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

// Tarihi backend formatına uygun şekilde normalize et
function formatForBackend(dateString) {
  if (!dateString) return '';
  
  // If it's already in YYYY-MM-DD format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
  
  // If it's in YYYY-MM-DDTHH:MM format, convert to YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/.test(dateString)) {
    return dateString.split('T')[0];
  }
  
  // If it's in YYYY-MM-DD HH:MM format, convert to YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(?::\d{2})?$/.test(dateString)) {
    return dateString.split(' ')[0];
  }
  
  // For any other format, try to parse and format
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (e) {
    console.error('Date parsing error:', e);
    return '';
  }
}

function LeaveApproval() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const isSmallScreen = useMediaQuery('(max-width: 600px)');

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await axios.get('https://pdks.bytebridge.com.tr/api/leave-requests');
      let data = response.data;
      // Farklı backend cevapları için normalize et
      if (!Array.isArray(data)) {
        data = data.leaveRequests || data.leave_requests || data.requests || data.data || [];
      }
      // Sadece bekleyen izin taleplerini göster
      const pending = data.filter(r => (r.LeaveRequestStatus || r.status || r.Status || r.leave_request_status) === 'Pending');
      setRequests(pending);
      setError(null);
    } catch (err) {
      setError('İzin talepleri alınırken hata oluştu: ' + (err?.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, []);

  const updateStatus = async (id, status) => {
    try {
      const req = requests.find(r => (r.RequestID || r.LeaveRequestID || r.ID || r.id) === id);
      if (!req) throw new Error('Talep bulunamadı');
      
      // Get the employee ID and ensure it's in the correct format
      let employeeId = req.EmployeeID || req.employee_id;
      
      // If employeeId is a string that looks like an integer, convert it to a number
      if (typeof employeeId === 'string' && /^\d+$/.test(employeeId)) {
        employeeId = parseInt(employeeId, 10);
      }
      
      const payload = {
        employee_id: employeeId,
        leave_type: req.LeaveType || req.leave_type,
        start_date: formatForBackend(req.StartDate || req.start_date),
        end_date: formatForBackend(req.EndDate || req.end_date),
        status,
      };
      
      for (const key of Object.keys(payload)) {
        if (!payload[key]) throw new Error(key + ' alanı boş veya geçersiz!');
      }
      
      console.log('PUT /leave-requests/' + id, payload);
      const res = await axios.put(`https://pdks.bytebridge.com.tr/api/leave-requests/${id}`, payload);
      if (res.data?.error) throw new Error(res.data.error);
      fetchRequests();
      setSuccess(true);
    } catch (err) {
      const detail = err?.response?.data?.error || err?.response?.data?.message || err?.message || '';
      setError('Durum güncellenirken hata oluştu: ' + detail);
      console.error('PUT leave-requests error:', err);
    }
  };

  const columns = [
    { field: 'LeaveRequestID', headerName: 'ID', flex: 0.5, minWidth: 70 },
    { field: 'EmployeeID', headerName: 'Çalışan ID', flex: 0.7, minWidth: 90 },
    { field: 'FirstName', headerName: 'Adı', flex: 1, minWidth: 100 },
    { field: 'LastName', headerName: 'Soyadı', flex: 1, minWidth: 100 },
    { field: 'LeaveType', headerName: 'İzin Türü', flex: 1, minWidth: 120 },
    { field: 'StartDate', headerName: 'Başlangıç Tarihi', flex: 1, minWidth: 120, valueGetter: (params) => formatDateTR(params.row.StartDate) },
    { field: 'EndDate', headerName: 'Bitiş Tarihi', flex: 1, minWidth: 120, valueGetter: (params) => formatDateTR(params.row.EndDate) },
    { field: 'LeaveRequestStatus', headerName: 'Durum', flex: 1, minWidth: 120 },
    {
      field: 'actions',
      headerName: 'İşlemler',
      width: isSmallScreen ? 120 : 160,
      renderCell: (params) => (
        <Box>
          <Button
            color="success"
            size="small"
            variant="contained"
            sx={{ mr: 1, minWidth: 70 }}
            onClick={() => updateStatus(params.row.RequestID, 'Approved')}
          >
            Onayla
          </Button>
          <Button
            color="error"
            size="small"
            variant="outlined"
            sx={{ minWidth: 70 }}
            onClick={() => updateStatus(params.row.RequestID, 'Rejected')}
          >
            Reddet
          </Button>
        </Box>
      ),
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <Tooltip title="İzin onay işlemlerini bu sayfadan yapabilirsiniz.">
          <InfoOutlinedIcon color="primary" style={{ marginRight: 8, cursor: 'pointer' }} />
        </Tooltip>
        <Typography variant="h4" gutterBottom>İzin Onaylama</Typography>
      </div>
      <Paper elevation={3} sx={{ backgroundColor: '#fff', width: '100%', overflowX: 'auto', mb: 3 }}>
        <Box sx={{ height: 500, width: '100%' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={requests}
              columns={columns}
              getRowId={(row) => row.RequestID}
              sx={{
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f5f5f5',
                  color: '#18191a',
                },
                '& .MuiDataGrid-cell': {
                  color: '#18191a',
                },
                bgcolor: 'transparent',
              }}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              loading={loading}
            />
          )}
        </Box>
      </Paper>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar open={success} autoHideDuration={6000} onClose={() => setSuccess(false)}>
        <Alert onClose={() => setSuccess(false)} severity="success" sx={{ width: '100%' }}>
          İzin isteği başarıyla güncellendi.
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default LeaveApproval;